import { apiCall } from "./api";
import { CENTRALISED_BILLING_API } from "./envConfig";

export const handleAddCustomPlanCredits = (
  workspaceId,
  { amount, credits, planCycle },
) => {
  return apiCall(`${CENTRALISED_BILLING_API}/custom/${workspaceId}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ amount, credits, planCycle }),
    credentials: "include",
  });
};

export const getBillingEvent = async (workspaceId) => {
  return apiCall(
    CENTRALISED_BILLING_API + "/billing-event/workspace/" + workspaceId,
  );
};

export const getFilteredAvailableCredits = async (
  workspaceId,
  { startDate, endDate } = {},
) => {
  const searchParams = new URLSearchParams();

  if (startDate) searchParams.set("startDate", startDate);
  if (endDate) searchParams.set("endDate", endDate);

  const query = searchParams.toString();

  return apiCall(
    `${CENTRALISED_BILLING_API}/credits/available-filtered/${workspaceId}${
      query ? `?${query}` : ""
    }`,
  );
};

export const getCreditUsageHistory = async (
  workspaceId,
  { types, startDate, endDate, limit, page, skip } = {},
) => {
  const searchParams = new URLSearchParams();

  if (types) searchParams.set("types", types);
  if (startDate) searchParams.set("startDate", startDate);
  if (endDate) searchParams.set("endDate", endDate);
  if (limit) searchParams.set("limit", String(limit));
  if (page) searchParams.set("page", String(page));
  if (typeof skip !== "undefined") searchParams.set("skip", String(skip));

  const query = searchParams.toString();

  return apiCall(
    `${CENTRALISED_BILLING_API}/credits/get-usage-history/${workspaceId}${
      query ? `?${query}` : ""
    }`,
  );
};

export const getPreviousBillingCycle = async (workspaceId) => {
  return apiCall(
    `${CENTRALISED_BILLING_API}/subscriptions/previous-billing-cycle/${workspaceId}`,
  );
};

export const getSubscriptions = async ({ isFreePlan } = {}) => {
  const searchParams = new URLSearchParams();

  if (typeof isFreePlan !== "undefined") {
    searchParams.set("isFreePlan", String(isFreePlan));
  }

  const query = searchParams.toString();

  return apiCall(
    `${CENTRALISED_BILLING_API}/subscriptions${query ? `?${query}` : ""}`,
  );
};

export const getPaidWorkspaces = async () => {
  return getSubscriptions({ isFreePlan: false });
};

export const getCurrentSubscription = async (workspaceId) => {
  return apiCall(
    `${CENTRALISED_BILLING_API}/subscriptions/current/${workspaceId}`,
  );
};

export const getCustomCreditInfo = async (workspaceId) => {
  return apiCall(`${CENTRALISED_BILLING_API}/custom/${workspaceId}`);
};

// Stripe locks a customer to the currency of their first paid invoice, so a
// price in any other currency is rejected with "The price specified only
// supports <x>. This doesn't match the expected currency: <y>." Plan lists must
// therefore be scoped to the workspace's registered billing currency.
//
// A custom plan records the currency the workspace was actually sold in, which
// can differ from the currency of the placeholder Stripe price it sits on, so
// it wins over the subscription's price. Returns null when the currency cannot
// be established — guessing "usd" here would filter an INR workspace down to
// exactly the prices Stripe will reject, which is the failure this scoping is
// meant to prevent. Callers must omit the filter rather than assume a default.
export const getWorkspaceBillingCurrency = async (workspaceId) => {
  const normalize = (currency) => {
    const normalized = String(currency ?? "").toLowerCase();
    return normalized === "inr" || normalized === "usd" ? normalized : null;
  };

  let customPlanCurrency = null;

  try {
    const customRes = await getCustomCreditInfo(workspaceId);
    customPlanCurrency = normalize(customRes?.data?.currency);
  } catch (error) {
    // A workspace without a custom plan 404s here; that is not an error.
    customPlanCurrency = null;
  }

  if (customPlanCurrency) {
    return customPlanCurrency;
  }

  try {
    const response = await getCurrentSubscription(workspaceId);

    return normalize(
      response?.data?.currency ||
        response?.data?.plan?.currency ||
        response?.data?.plan?.metadata?.currency,
    );
  } catch (error) {
    console.error("Error resolving workspace billing currency:", error);
    return null;
  }
};

export const getStripeProducts = async ({ type = "all", currency } = {}) => {
  const searchParams = new URLSearchParams();

  if (type) searchParams.set("type", type);
  if (currency) searchParams.set("currency", String(currency).toLowerCase());

  const query = searchParams.toString();

  return apiCall(
    `${CENTRALISED_BILLING_API}/subscriptions/products${
      query ? `?${query}` : ""
    }`,
  );
};

// An unknown currency must widen the catalog, never narrow it to a guess: a
// workspace whose currency we could not establish is shown every price rather
// than only the USD ones.
export const buildProductsUrl = (currency) => {
  return `${CENTRALISED_BILLING_API}/subscriptions/products${
    currency ? `?currency=${String(currency).toLowerCase()}` : ""
  }`;
};

export const getBillingAlerts = async (
  {
    status,
    alertType,
    severity,
    workspaceId,
    customerId,
    startDate,
    endDate,
    limit,
    skip,
    page,
  } = {},
) => {
  const searchParams = new URLSearchParams();

  if (status) searchParams.set("status", status);
  if (alertType) searchParams.set("alertType", alertType);
  if (severity) searchParams.set("severity", severity);
  if (workspaceId) searchParams.set("workspaceId", workspaceId);
  if (customerId) searchParams.set("customerId", customerId);
  if (startDate) searchParams.set("startDate", startDate);
  if (endDate) searchParams.set("endDate", endDate);
  if (limit) searchParams.set("limit", String(limit));
  if (typeof skip !== "undefined") searchParams.set("skip", String(skip));
  if (page) searchParams.set("page", String(page));

  const query = searchParams.toString();

  return apiCall(
    `${CENTRALISED_BILLING_API}/billing-alerts${query ? `?${query}` : ""}`,
  );
};

export const performBillingAlertAction = async (alertId, action, metadata = {}) => {
  return apiCall(`${CENTRALISED_BILLING_API}/billing-alerts/${alertId}/actions`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action, metadata }),
  });
};

export const previewBillingAlertAction = async (alertId, action, metadata = {}) => {
  return apiCall(
    `${CENTRALISED_BILLING_API}/billing-alerts/${alertId}/actions/preview`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ action, metadata }),
    },
  );
};

export const getRollOverList = async (workspaceId) => {
  return apiCall(`${CENTRALISED_BILLING_API}/rollover/${workspaceId}`);
};

export const getRollOverPreview = async (workspaceId) => {
  return apiCall(`${CENTRALISED_BILLING_API}/rollover/${workspaceId}/preview`);
};

export const applyRollOver = async (workspaceId, body) => {
  return apiCall(`${CENTRALISED_BILLING_API}/rollover/${workspaceId}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
};

// Provision only the known missing-workspace response, never an outage.
export const isMissingBillingWorkspace = (error) =>
  (error?.status == null || error.status === 404) &&
  (error?.body?.error ?? error?.error) === "Workspace not found";
